import { getTimelineStatusAt as getTimelineStatus } from '@/helpers/ssr/dates'

import {
  getPricingTypeLabels,
  getTypeDescription,
} from '@/lib/usaspending/awardTypeMappings'

import type {
  Award,
  FederalAccount,
  Location,
  ParentAward,
  Person,
  ProcurementDetails,
  ProgramDetails,
  SubAward,
  Transaction,
} from '@/types/usaspending'

import type { BadgeVariant } from '../../common/BadgeV2'
import {
  type NormalizedPlaceOfPerformance,
  formatCompactCurrency,
  formatCurrency,
  formatDate,
  normalizePlaceOfPerformance,
} from '../../common/transforms'

// ============================================================================
// Types
// ============================================================================

export interface TimelineEvent {
  id: string
  date: string
  label: string
  status: 'past' | 'current' | 'future'
  variant?: 'default' | 'start' | 'deadline' | 'modification'
}

export interface NormalizedAwardData {
  // Award IDs
  id: string
  generatedId: string
  piid: string | null
  fain: string | null
  uri: string | null
  parentAwardPiid: string | null
  parentAwardGeneratedUniqueId: string | null

  // Header
  title: string
  subtitle: string // Agency name
  badges: Array<{
    label: string
    variant: BadgeVariant
  }>

  // Key Stats (for DataBlockV2 grid)
  stats: {
    category: string | null
    type: string | null
    typeDescription: string | null
    pricingType: string | null
    pricingTypeShort: string | null
    pricingTypeDescription: string | null
    status: 'active' | 'closed' | 'potential'
  }

  // Financials
  financials: {
    obligatedAmount: number
    currentAwardAmount: number
    potentialAwardAmount: number
    nonFederalFunding: number
    totalOutlayedAmount: number
    obligatedFormatted: string | null
    currentFormatted: string | null
    potentialFormatted: string | null
    outlayedFormatted: string | null
    percentObligated: number
  }

  // Dates
  dates: {
    startDate: string | null
    startDateFormatted: string | null
    endDate: string | null
    endDateFormatted: string | null
    potentialEndDate: string | null
    potentialEndDateFormatted: string | null
    lastModifiedDate: string | null
    lastModifiedDateFormatted: string | null
    dateSigned: string | null
    dateSignedFormatted: string | null
    actionDate: string | null
    actionDateFormatted: string | null
  }

  // Timeline
  timeline: TimelineEvent[]

  // Agencies
  awardingAgency: {
    id: string | null
    name: string
    subTier: string
    office: string
  }
  fundingAgency: {
    id: string | null
    name: string
    subTier: string
    office: string
  }

  // Recipient
  recipient: {
    id: string | null
    name: string
    uei: string
    duns: string | null
    cageCode: string | null
    // Formatted location string for display
    location: string | null
    // Full address components
    address: {
      streetAddress: string | null
      city: string | null
      state: string | null
      zip: string | null
      country: string | null
      congressionalDistrict: string | null
    }
    businessTypes: string[]
    parentName: string | null
    parentId: string | null
  }

  // Place of Performance (uses shared normalized type)
  placeOfPerformance: NormalizedPlaceOfPerformance

  // Classification Codes
  codes: {
    naicsCode: string | null
    naicsDescription: string | null
    pscCode: string | null
    pscDescription: string | null
    // CFDA/Assistance Listing (for assistance awards)
    cfdaNumber: string | null
    cfdaTitle: string | null
    cfdaNumbers: string[]
  }

  // Content
  description: string | null

  // Federal Accounts
  federalAccounts: Array<{
    id: string
    title: string
    agencyIdentifier: string
    mainAccountCode: string
    accountType: string
  }>

  // Transaction History
  transactions: Array<{
    modNumber: string
    actionDate: string
    actionDateFormatted: string | null
    actionType: string
    federalActionObligation: number
    obligationFormatted: string | null
    description: string
  }>

  // Sub-Awards
  subAwards: Array<{
    id: string
    recipientName: string
    amount: number
    amountFormatted: string | null
    date: string
    dateFormatted: string | null
    description: string
  }>

  // Contacts (from recipient officers)
  officers: Array<{
    id: string | null
    name: string
    title: string | null
    role: string | null
  }>

  // Solicitation point of contact
  solicitationContact: {
    id: string | null
    name: string
    title: string | null
    email: string | null
    phone: string | null
  } | null

  // Linked Solicitation
  solicitationId: string | null
  solicitation: {
    id: string
    solicitationNumber: string
    title: string
    description: string | null
    source: string
    postedDate: string | null
    postedDateFormatted: string | null
    responseDeadline: string | null
    responseDeadlineFormatted: string | null
    archiveDate: string | null
    archiveDateFormatted: string | null
    // Contract-specific fields
    noticeType: string | null
    naicsCode: string | null
    naicsName: string | null
    pscCode: string | null
    pscName: string | null
    setAside: string | null
    // Agency info
    agencyName: string | null
    officeName: string | null
    link: string | null
  } | null

  // Procurement details (FPDS contracts only)
  procurementDetails: ProcurementDetails | null

  // Program details (assistance awards only: grants, loans, etc.)
  programDetails: ProgramDetails | null

  // Parent IDV details (contract orders only)
  parentAward: ParentAward | null
}

// ============================================================================
// Domain-Specific Utilities
// ============================================================================

/**
 * Map award category to badge variant
 */
function getCategoryVariant(category: string | null): BadgeVariant {
  if (!category) return 'outline'
  const catLower = category.toLowerCase()
  if (catLower === 'contract') return 'info'
  if (catLower === 'grant') return 'success'
  if (catLower === 'loan') return 'warning'
  if (catLower === 'idv') return 'neutral'
  return 'outline'
}

/**
 * Determine award status based on dates
 */
function getAwardStatus(
  endDate: string | null | undefined,
  potentialEndDate: string | null | undefined,
  referenceMs: number,
): 'active' | 'closed' | 'potential' {
  if (endDate) {
    const end = new Date(endDate)
    if (end.getTime() < referenceMs) return 'closed'
  }

  if (potentialEndDate) {
    const potential = new Date(potentialEndDate)
    if (potential.getTime() > referenceMs) return 'potential'
  }

  return 'active'
}

/**
 * Get status badge variant
 */
function getStatusVariant(
  status: 'active' | 'closed' | 'potential',
): BadgeVariant {
  switch (status) {
    case 'active':
      return 'success'
    case 'closed':
      return 'neutral'
    case 'potential':
      return 'warning'
  }
}

/**
 * Format location into single string
 */
function formatLocation(location: Location | undefined): string | null {
  if (!location) return null
  const parts = [
    location.city,
    location.state,
    location.country !== 'USA' && location.country !== 'US'
      ? location.country
      : null,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : null
}

// ============================================================================
// Main Transform
// ============================================================================

/**
 * Normalize raw Award data into a format suitable for DisplayV2 components
 */
export function normalizeAwardData(
  award: Award,
  referenceMs: number,
): NormalizedAwardData {
  const status = getAwardStatus(
    award.dates?.endDate,
    award.dates?.potentialEndDate,
    referenceMs,
  )

  // Build badges
  const badges: Array<{ label: string; variant: BadgeVariant }> = []

  // Category badge (Contract, Grant, etc.)
  if (award.category) {
    badges.push({
      label: award.category.charAt(0).toUpperCase() + award.category.slice(1),
      variant: getCategoryVariant(award.category),
    })
  }

  // Status badge
  badges.push({
    label: status.charAt(0).toUpperCase() + status.slice(1),
    variant: getStatusVariant(status),
  })

  // Pricing type badge if available (use short label for badge)
  const pricingTypeLabels = getPricingTypeLabels(award.pricingType)
  if (pricingTypeLabels.short) {
    badges.push({
      label: pricingTypeLabels.short,
      variant: 'outline',
    })
  }

  // Build timeline
  const timeline: TimelineEvent[] = []

  // Date signed
  if (award.dates?.dateSigned) {
    timeline.push({
      id: 'signed',
      date: award.dates.dateSigned,
      label: 'Date Signed',
      status: getTimelineStatus(award.dates.dateSigned, referenceMs),
      variant: 'start',
    })
  }

  // Start date
  if (award.dates?.startDate) {
    timeline.push({
      id: 'start',
      date: award.dates.startDate,
      label: 'Period Start',
      status: getTimelineStatus(award.dates.startDate, referenceMs),
      variant: 'default',
    })
  }

  // End date
  if (award.dates?.endDate) {
    timeline.push({
      id: 'end',
      date: award.dates.endDate,
      label: 'Period End',
      status: getTimelineStatus(award.dates.endDate, referenceMs, true),
      variant: 'deadline',
    })
  }

  // Potential end date
  if (
    award.dates?.potentialEndDate &&
    award.dates.potentialEndDate !== award.dates.endDate
  ) {
    timeline.push({
      id: 'potential-end',
      date: award.dates.potentialEndDate,
      label: 'Potential End',
      status: getTimelineStatus(award.dates.potentialEndDate, referenceMs),
      variant: 'default',
    })
  }

  // Sort timeline by date
  timeline.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )

  // Calculate percent obligated
  const potentialAmount = award.financials?.potentialAwardAmount || 0
  const obligatedAmount = award.financials?.obligatedAmount || 0
  const percentObligated =
    potentialAmount > 0 ? (obligatedAmount / potentialAmount) * 100 : 0

  // Normalize transactions
  const transactions =
    award.history?.map((tx: Transaction) => ({
      modNumber: tx.modNumber,
      actionDate: tx.actionDate,
      actionDateFormatted: formatDate(tx.actionDate),
      actionType: tx.actionType,
      federalActionObligation: tx.federalActionObligation,
      obligationFormatted: formatCurrency(tx.federalActionObligation),
      description: tx.description,
    })) ?? []

  // Normalize sub-awards
  const subAwards =
    award.subAwards?.map((sub: SubAward) => ({
      id: sub.id,
      recipientName: sub.subAwardee?.name || 'Unknown',
      amount: sub.amount,
      amountFormatted: formatCurrency(sub.amount),
      date: sub.date,
      dateFormatted: formatDate(sub.date),
      description: sub.description,
    })) ?? []

  // Normalize federal accounts
  const federalAccounts =
    award.federalAccounts?.map((acc: FederalAccount) => ({
      id: acc.id,
      title: acc.title,
      agencyIdentifier: acc.agencyIdentifier,
      mainAccountCode: acc.mainAccountCode,
      accountType: acc.accountType,
    })) ?? []

  // Normalize recipient officers
  const officers =
    award.recipient?.officers?.map((officer: Person) => ({
      id: officer.id || null,
      name: officer.fullName,
      title: officer.title || null,
      role: officer.role || null,
    })) ?? []

  // Normalize solicitation point of contact
  const sol = award.solicitation
  const solicitationContact = sol?.pointOfContact
    ? {
        id: sol.pointOfContact.id || null,
        name: sol.pointOfContact.fullName,
        title: sol.pointOfContact.title || null,
        email: sol.pointOfContact.email || null,
        phone: sol.pointOfContact.phone || null,
      }
    : null

  // Build solicitation if available
  const solicitation = sol
    ? {
        id: sol.id,
        solicitationNumber: sol.solicitationNumber,
        title: sol.title,
        description: sol.description || null,
        source: sol.source,
        postedDate: sol.postedDate || null,
        postedDateFormatted: formatDate(sol.postedDate),
        responseDeadline: sol.responseDeadline || null,
        responseDeadlineFormatted: formatDate(sol.responseDeadline),
        archiveDate: sol.archiveDate || null,
        archiveDateFormatted: formatDate(sol.archiveDate),
        // Contract-specific fields (from ContractSolicitation)
        noticeType:
          sol.source === 'SAM.gov' && 'noticeType' in sol
            ? sol.noticeType
            : null,
        naicsCode:
          sol.source === 'SAM.gov' && 'naics' in sol ? sol.naics?.code : null,
        naicsName:
          sol.source === 'SAM.gov' && 'naics' in sol ? sol.naics?.name : null,
        pscCode:
          sol.source === 'SAM.gov' && 'psc' in sol ? sol.psc?.code : null,
        pscName:
          sol.source === 'SAM.gov' && 'psc' in sol ? sol.psc?.name : null,
        setAside:
          sol.source === 'SAM.gov' && 'setAside' in sol ? sol.setAside : null,
        // Agency info (not available on Solicitation type, comes from awardingAgency)
        agencyName: null,
        officeName: null,
        link: sol.link || null,
      }
    : null

  // Build subtitle from awarding agency
  const subtitle = [
    award.awardingAgency?.name,
    award.awardingAgency?.subTier !== award.awardingAgency?.name
      ? award.awardingAgency?.subTier
      : null,
  ]
    .filter(Boolean)
    .join(' - ')

  return {
    id: award.id,
    generatedId: award.generatedUniqueAwardId,
    piid: award.piid || null,
    fain: award.fain || null,
    uri: award.uri || null,
    parentAwardPiid: award.parentAwardPiid || null,
    parentAwardGeneratedUniqueId: award.parentAwardGeneratedUniqueId || null,

    title: award.title || 'Federal Award',
    subtitle: subtitle || 'Federal Agency',
    badges,

    stats: {
      category: award.category || null,
      type: award.type || null,
      // Use API-provided typeDescription, fall back to mapped description from type code
      typeDescription:
        award.typeDescription ||
        (award.type ? getTypeDescription(award.type) : null),
      pricingType: award.pricingType || null,
      pricingTypeShort: pricingTypeLabels.short,
      pricingTypeDescription: pricingTypeLabels.full,
      status,
    },

    financials: {
      obligatedAmount: award.financials?.obligatedAmount ?? 0,
      currentAwardAmount: award.financials?.currentAwardAmount ?? 0,
      potentialAwardAmount: award.financials?.potentialAwardAmount ?? 0,
      nonFederalFunding: award.financials?.nonFederalFunding ?? 0,
      totalOutlayedAmount: award.financials?.totalOutlayedAmount ?? 0,
      obligatedFormatted: formatCurrency(award.financials?.obligatedAmount),
      currentFormatted: formatCurrency(award.financials?.currentAwardAmount),
      potentialFormatted: formatCurrency(
        award.financials?.potentialAwardAmount,
      ),
      outlayedFormatted: formatCurrency(award.financials?.totalOutlayedAmount),
      percentObligated,
    },

    dates: {
      startDate: award.dates?.startDate || null,
      startDateFormatted: formatDate(award.dates?.startDate),
      endDate: award.dates?.endDate || null,
      endDateFormatted: formatDate(award.dates?.endDate),
      potentialEndDate: award.dates?.potentialEndDate || null,
      potentialEndDateFormatted: formatDate(award.dates?.potentialEndDate),
      lastModifiedDate: award.dates?.lastModifiedDate || null,
      lastModifiedDateFormatted: formatDate(award.dates?.lastModifiedDate),
      dateSigned: award.dates?.dateSigned || null,
      dateSignedFormatted: formatDate(award.dates?.dateSigned),
      actionDate: award.dates?.actionDate || null,
      actionDateFormatted: formatDate(award.dates?.actionDate),
    },

    timeline,

    awardingAgency: {
      id: award.awardingAgency?.id || null,
      name: award.awardingAgency?.name || '',
      subTier: award.awardingAgency?.subTier || '',
      office: award.awardingAgency?.office || '',
    },

    fundingAgency: {
      id: award.fundingAgency?.id || null,
      name: award.fundingAgency?.name || '',
      subTier: award.fundingAgency?.subTier || '',
      office: award.fundingAgency?.office || '',
    },

    recipient: {
      id: award.recipient?.id || null,
      name: award.recipient?.name || '',
      uei: award.recipient?.uei || '',
      duns: award.recipient?.duns || null,
      cageCode: award.recipient?.cageCode || null,
      location: formatLocation(award.recipient?.location),
      address: {
        streetAddress: award.recipient?.location?.streetAddress || null,
        city: award.recipient?.location?.city || null,
        state: award.recipient?.location?.state || null,
        zip: award.recipient?.location?.zip || null,
        country: award.recipient?.location?.country || null,
        congressionalDistrict:
          award.recipient?.location?.congressionalDistrict || null,
      },
      businessTypes: award.recipient?.businessTypes || [],
      parentName: award.recipient?.parent?.name || null,
      parentId: award.recipient?.parent?.id || null,
    },

    // Use shared PoP normalizer for consistent formatting
    placeOfPerformance: normalizePlaceOfPerformance(award.placeOfPerformance),

    codes: {
      naicsCode: award.codes?.naicsCode || null,
      naicsDescription: award.codes?.naicsDescription || null,
      pscCode: award.codes?.pscCode || null,
      pscDescription: award.codes?.pscDescription || null,
      cfdaNumber: award.codes?.cfdaNumber || null,
      cfdaTitle: award.codes?.cfdaTitle || null,
      cfdaNumbers: award.codes?.cfdaNumbers || [],
    },

    description: award.description || null,

    federalAccounts,
    transactions,
    subAwards,
    officers,
    solicitationContact,
    solicitationId: award.solicitationId || null,
    solicitation,
    procurementDetails: award.procurementDetails || null,
    programDetails: award.programDetails || null,
    parentAward: award.parentAward || null,
  }
}
